import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	AccountSummaryRow,
	CloseDateRow,
	CostCenterOption,
} from "../models/SummaryByCloseDate";

export const getCostCenters = async (
	signal?: AbortSignal,
): Promise<CostCenterOption[]> => {
	const apiService = new MESApiService();
	// The legacy screen sends nothing and gets the active cost centers; OnlyActives
	// is a BIT, and the executor rejects a number for one.
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2(
		"[MILL].[GetCostCenters]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching cost centers");
	}
	return get(resp, "data.tables[0].rows", []) as CostCenterOption[];
};

export const getCloseDates = async (
	costCenterId: number,
	signal?: AbortSignal,
): Promise<CloseDateRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "costCenterId", value: costCenterId },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetAccountSummaryCloseDates]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching close dates");
	}
	return get(resp, "data.tables[0].rows", []) as CloseDateRow[];
};

export const getAccountSummaryByClose = async (
	costCenterId: number,
	close: string,
	signal?: AbortSignal,
): Promise<AccountSummaryRow | null> => {
	const apiService = new MESApiService();
	// The close travels as the MM/DD/YYYY string the close dates procedure produced;
	// that is the shape the stored close is compared against on both branches.
	const parameters: spParameter[] = [
		{ name: "costCenterId", value: costCenterId },
		{ name: "close", value: close },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetAccountSummaryByClose]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching the account summary");
	}
	const rows = get(resp, "data.tables[0].rows", []) as AccountSummaryRow[];
	// The legacy branch answers an unknown close with one row of nulls, the new one
	// with no rows at all. Both mean the same thing.
	const row = rows.length > 0 ? rows[0] : null;
	return row && row.TotalCost != null ? row : null;
};
